import Decimal from 'decimal.js';

/**
 * Utility functions for precise decimal arithmetic to avoid floating-point errors
 */

/**
 * Add two decimal numbers with precision
 * @param {number|string} a 
 * @param {number|string} b 
 * @returns {number} Result with proper precision
 */
export const addDecimal = (a, b) => {
  const aCents = Math.round((parseFloat(a) || 0) * 100);
  const bCents = Math.round((parseFloat(b) || 0) * 100);
  return (aCents + bCents) / 100;
};

/**
 * Subtract two decimal numbers with precision
 * @param {number|string} a 
 * @param {number|string} b 
 * @returns {number} Result with proper precision
 */
export const subtractDecimal = (a, b) => {
  const aCents = Math.round((parseFloat(a) || 0) * 100);
  const bCents = Math.round((parseFloat(b) || 0) * 100);
  return (aCents - bCents) / 100;
};

/**
 * Calculate balance change from credit and debit values
 * @param {number|string} credit 
 * @param {number|string} debit 
 * @returns {number} Balance change with proper precision
 */
export const calculateBalanceChange = (credit, debit) => {
  const creditCents = Math.round((parseFloat(credit) || 0) * 100);
  const debitCents = Math.round((parseFloat(debit) || 0) * 100);
  return (creditCents - debitCents) / 100;
};

/**
 * Format a number to exactly 2 decimal places
 * @param {number|string} value 
 * @returns {number} Formatted number
 */
export const formatDecimal = (value) => {
  return parseFloat((parseFloat(value) || 0).toFixed(2));
};

/**
 * Compute the net amount as credit - debit using decimal.js for precision.
 * @param {number|string} credit
 * @param {number|string} debit
 * @returns {{ value: number, formatted: string, isNegative: boolean, isZero: boolean }}
 */
export const computeAmount = (credit, debit) => {
  const zero = { value: 0, formatted: '$0.00', isNegative: false, isZero: true };
  try {
    const c = new Decimal(parseFloat(credit) || 0);
    const d = new Decimal(parseFloat(debit) || 0);
    const result = c.minus(d);
    const value = result.toNumber();
    const isNegative = result.isNegative() && !result.isZero();
    const isZero = result.isZero();
    const abs = Number(result.abs().toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const formatted = isNegative ? `($${abs})` : `$${abs}`;
    return { value, formatted, isNegative, isZero };
  } catch {
    return zero;
  }
};

/**
 * Format a single signed amount as v3 currency (see DESIGN_SYSTEM.md §10).
 * Two decimals, comma thousands, accounting parens for negatives, plain $0.00 for
 * zero (never ($0.00)). Never a minus glyph or hyphen.
 * @param {number|string} amount
 * @returns {string} e.g. "$1,234.56", "($1,400.00)", "$0.00"
 */
export const formatCurrency = (amount) => {
  let n = parseFloat(amount);
  if (!Number.isFinite(n)) n = 0;
  if (Object.is(n, -0)) n = 0;
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${abs})` : `$${abs}`;
};

/**
 * Parse a user-typed amount string, stripping thousands separators and $ before
 * parsing so "16,331.40" / "$16,331.40" / "$ 16,331.40" parse to the intended value
 * instead of truncating at the first comma (raw parseFloat("16,331.40") returns 16).
 * Empty/nullish/garbage input degrades to 0 (never NaN).
 * @param {number|string} raw
 * @returns {number}
 */
export const parseAmountInput = (raw) => {
  if (raw === '' || raw == null) return 0;
  const cleaned = String(raw).replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Compute running balances for an array of v2 line items (newest first).
 * The first element equals currentBalance; each subsequent element subtracts
 * the previous line's net contribution (amountCents / 100).
 * @param {number|string} currentBalance
 * @param {Array<{amountCents?: number}>} lines
 * @returns {number[]}
 */
export const computeRunningBalances = (currentBalance, lines) => {
  if (!lines || lines.length === 0) return [];

  const balances = new Array(lines.length);
  let running = new Decimal(parseFloat(currentBalance) || 0);
  balances[0] = running.toNumber();

  for (let i = 1; i < lines.length; i++) {
    const prev = lines[i - 1];
    const cents = Number.isFinite(prev?.amountCents) ? prev.amountCents : 0;
    const net = new Decimal(cents).dividedBy(100);
    running = running.minus(net);
    balances[i] = running.toNumber();
  }

  return balances;
};


export default {
  addDecimal,
  subtractDecimal,
  calculateBalanceChange,
  formatDecimal,
  computeAmount,
  formatCurrency,
  parseAmountInput,
  computeRunningBalances
};